import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Multiselect } from "react-widgets";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldLabel,
  getFieldValue,
  getFieldOptions,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormCheckbox,
  FormInput,
  FormRadioButton,
} from "../common/ControlBundle";
import ExpandDiv from "../common/ExpandDiv";
import FormButtonBar from "../common/FormButtonBar";
import LineGraph from "../common/LineGraph";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function SAFYPFS2Assessment() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.value;
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram.value
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram.value = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.value) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [selectedAssessmentDates, setSelectedAssessmentDates] = useState([]);

  const allAssessmentDatesUnique = useMemo(() => {
    if (allFormSubmissions === undefined) {
      return [];
    }

    return allFormSubmissions
      .filter(
        (submission) =>
          (submission.Active === "1" && submission.FinalSigned === "1") ||
          submission.SubmissionId === submissionid
      )
      .map((submission) =>
        moment(
          getFieldValue(submission.FormResults.results.DateCompleted)
        ).format("l")
      )
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => moment(a) - moment(b));
  }, [allFormSubmissions, submissionid]);

  useEffect(() => {
    setSelectedAssessmentDates(
      allFormSubmissions
        .filter(
          (submission) =>
            (submission.Active === "1" && submission.FinalSigned === "1") ||
            submission.SubmissionId === submissionid
        )
        .map((submission) =>
          moment(
            getFieldValue(submission.FormResults.results.DateCompleted)
          ).format("l")
        )
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort((a, b) => moment(a) - moment(b))
    );
  }, [allFormSubmissions, submissionid]);

  const graphOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        font: {
          size: 16,
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        min: 0,
        max: 4,
        ticks: {
          stepSize: 1,
        },
        font: {
          size: 16,
        },
      },
    },
  };

  const chartData = useMemo(() => {
    if (allFormSubmissions === undefined || !data.DateCompleted?.value) {
      return;
    }

    const rankings = { A: 0, B: 1, C: 2, D: 3, E: 4 };
    const inverseRankings = { A: 4, B: 3, C: 2, D: 1, E: 0 };

    const getFamilyFunctioningScore = (dataObj) => {
      // the first letter of each value is A-E, so we can use the first letter to determine the score
      let familyFunctioningScore = 0;
      const values = [
        getFieldValue(dataObj.GoodFuture),
        getFieldValue(dataObj.FamilyListens),
        getFieldValue(dataObj.SpecialMoments),
      ].filter((v) => v);
      values.forEach((v) => {
        familyFunctioningScore += rankings[v[0]];
      });
      return familyFunctioningScore / 3;
    };

    const getNurturingScore = (dataObj) => {
      // the first letter of each value is A-E, so we can use the first letter to determine the score
      let nurturingScore = 0;
      const values = [
        getFieldValue(dataObj.IntentToMisbehave),
        getFieldValue(dataObj.ConstantStop),
        getFieldValue(dataObj.PowerStruggles),
        getFieldValue(dataObj.EmotionalResponse),
      ].filter((v) => v);
      values.forEach((v) => {
        nurturingScore += inverseRankings[v[0]];
      });
      return nurturingScore / 4;
    };

    const getSocialSupportsScore = (dataObj) => {
      // the first letter of each value is A-E, so we can use the first letter to determine the score
      let socialSupportsScore = 0;
      const values = [
        getFieldValue(dataObj.BeliefInMe),
        getFieldValue(dataObj.LifeAdvice),
        getFieldValue(dataObj.SupportiveFriends),
        getFieldValue(dataObj.ShortNotice),
      ].filter((v) => v);
      values.forEach((v) => {
        socialSupportsScore += rankings[v[0]];
      });

      let additionalSupport = 0;
      if (getFieldValue(dataObj.AdviceNone) !== "true") {
        if (getFieldValue(dataObj.AdviceMoney) === "true") {
          additionalSupport++;
        }
        if (getFieldValue(dataObj.AdviceFood) === "true") {
          additionalSupport++;
        }
        if (getFieldValue(dataObj.AdviceParenting) === "true") {
          additionalSupport++;
        }
        if (getFieldValue(dataObj.AdviceRelationships) === "true") {
          additionalSupport++;
        }
        if (getFieldValue(dataObj.AdviceStress) === "true") {
          additionalSupport++;
        }

        socialSupportsScore += additionalSupport > 4 ? 4 : additionalSupport;
      }

      return socialSupportsScore / 5;
    };

    const getRelationshipScore = (dataObj) => {
      let relationshipScore = 0;

      relationshipScore += getFieldValue(dataObj.StaffUnderstands)
        ? inverseRankings[getFieldValue(dataObj.StaffUnderstands)[0]]
        : 0;

      const values = [
        getFieldValue(dataObj.NoBelief),
        getFieldValue(dataObj.Misunderstood),
      ].filter((v) => v);
      values.forEach((v) => {
        relationshipScore += rankings[v[0]];
      });
      return relationshipScore / 3;
    };

    const getConcreteScore = (dataObj) => {
      let concreteScore = 0;
      concreteScore += getFieldValue(dataObj.TroubleAffording)
        ? inverseRankings[getFieldValue(dataObj.TroubleAffording)[0]]
        : 0;
      concreteScore += getFieldValue(dataObj.AffordFood)
        ? rankings[getFieldValue(dataObj.AffordFood)[0]]
        : 0;

      if (getFieldValue(dataObj.PayAll) === "true") {
        concreteScore += 4;
      } else {
        let selectedCheck = false;
        let unableDeficit = 4;
        if (getFieldValue(dataObj.PayRent) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PayDaycare) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PayTransportation) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PayUtilities) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PayMedical) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PayGroceries) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PayBasic) === "true") {
          unableDeficit--;
          selectedCheck = true;
        }
        concreteScore += selectedCheck
          ? unableDeficit < 0
            ? 0
            : unableDeficit
          : 0;
      }

      if (getFieldValue(dataObj.PastNone) === "true") {
        concreteScore += 4;
      } else {
        let selectedCheck = false;
        let pastDeficit = 4;
        if (getFieldValue(dataObj.PastDelay) === "true") {
          pastDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PastShelter) === "true") {
          pastDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PastTransporation) === "true") {
          pastDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PastEvicted) === "true") {
          pastDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PastRoommate) === "true") {
          pastDeficit--;
          selectedCheck = true;
        }
        if (getFieldValue(dataObj.PastUnemployment) === "true") {
          pastDeficit--;
          selectedCheck = true;
        }
        concreteScore += selectedCheck
          ? pastDeficit < 0
            ? 0
            : pastDeficit
          : 0;
      }

      return concreteScore / 4;
    };

    const selectedAssessments = allFormSubmissions
      .filter(
        (assessment) =>
          assessment.SubmissionId !== formsyDetails.SubmissionId &&
          assessment.Active === "1" &&
          assessment.FinalSigned === "1" &&
          assessment.FormResults.results.DateCompleted &&
          selectedAssessmentDates.includes(
            moment(
              getFieldValue(assessment.FormResults.results.DateCompleted)
            ).format("l")
          )
      )
      .sort(
        (a, b) =>
          moment(getFieldValue(a.FormResults.results.DateCompleted)) -
          moment(getFieldValue(b.FormResults.results.DateCompleted))
      );

    const labels = [
      ...selectedAssessmentDates.map((date) => moment(date).format("l")),
    ];

    if (selectedAssessmentDates.includes(data.DateCompleted.value)) {
      labels.push(moment(data.DateCompleted.value).format("l"));
    }

    selectedAssessments.push({
      FormResults: {
        results: {
          ...data,
        },
      },
    });

    return {
      labels: labels,
      datasets: [
        {
          label: "Family Functioning / Resilience",
          data: [
            ...selectedAssessments.map((assessment) =>
              getFamilyFunctioningScore(assessment.FormResults.results)
            ),
          ],
        },
        {
          label: "Nurturing & Attachment",
          data: [
            ...selectedAssessments.map((assessment) =>
              getNurturingScore(assessment.FormResults.results)
            ),
          ],
        },
        {
          label: "Social Supports",
          data: [
            ...selectedAssessments.map((assessment) =>
              getSocialSupportsScore(assessment.FormResults.results)
            ),
          ],
        },
        {
          label: "Caregiver / Practitioner Relationship",
          data: [
            ...selectedAssessments.map((assessment) =>
              getRelationshipScore(assessment.FormResults.results)
            ),
          ],
        },
        {
          label: "Concrete Supports",
          data: [
            ...selectedAssessments.map((assessment) =>
              getConcreteScore(assessment.FormResults.results)
            ),
          ],
        },
      ],
    };
  }, [data, formsyDetails, allFormSubmissions, selectedAssessmentDates]);

  useEffect(() => {
    if (clientData.client) {
      setData((d) => {
        const obj = { ...d };

        if (d.ParticipantId) {
          obj.ParticipantId.value = `${clientData.client.lastName} - ${clientData.client.mpi}`;
        }

        return obj;
      });
    }
  }, [clientData.client]);

  ////UI
  return (
    <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          hideHeaderOnPrint={true}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          printStyle={`
          .bg-pfs2 {
              background-color: #00a74d;
          }`}
          printColor={true}
        />
        <div className="py-2 border-bottom border-dark d-print-none">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="container text-center text-white bg-pfs2">
                <div className="row">
                  <div className="col pb-2"></div>
                  <h1>
                    <b>{getFieldLabel(formConfig.Header)}</b>
                    <br />
                    {data.Header?.sub}
                  </h1>
                </div>
              </div>
              <div className="container">
                <div className="row">
                  <div className="col py-2">
                    <FormInput
                      name="AgencyId"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.AgencyId)}
                        </span>
                      }
                      error={errors.AgencyId}
                      value={getFieldValue(data.AgencyId)}
                      onChange={handleChange}
                      identical={compare("AgencyId")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormInput
                      name="ParticipantId"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.ParticipantId)}
                        </span>
                      }
                      error={errors.ParticipantId}
                      value={getFieldValue(data.ParticipantId)}
                      onChange={handleChange}
                      identical={compare("ParticipantId")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormInput
                      name="DateCompleted"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.DateCompleted)}
                        </span>
                      }
                      error={errors.DateCompleted}
                      value={getFieldValue(data.DateCompleted)}
                      onChange={handleChange}
                      identical={compare("DateCompleted")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-8 py-2">
                    <FormRadioButton
                      name="TestType"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.TestType)}
                        </span>
                      }
                      error={errors.TestType}
                      value={getFieldValue(data.TestType)}
                      options={[getFieldOptions(formConfig.TestType)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col-4 py-2">
                    <FormInput
                      name="ProgramStartDate"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.ProgramStartDate)}
                        </span>
                      }
                      error={errors.ProgramStartDate}
                      value={
                        !getFieldValue(data.ProgramStartDate) &&
                        clientData?.programs &&
                        data.SelectedProgram.value
                          ? moment(
                              getFieldDate(
                                clientData.programs.filter(
                                  (option) =>
                                    option.ccx_client_programid ===
                                    getFieldValue(data.SelectedProgram)
                                )[0].ccx_admitdate
                              )
                            ).format(moment.HTML5_FMT.DATE)
                          : getFieldValue(data.ProgramStartDate)
                      }
                      onChange={handleChange}
                      identical={compare("ProgramStartDate")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-8 py-2">
                    <FormRadioButton
                      name="TestType"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.TestType)}
                        </span>
                      }
                      error={errors.TestType}
                      value={getFieldValue(data.TestType)}
                      options={[getFieldOptions(formConfig.TestType)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormInput
                      name="ProgramDateCompleted"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.ProgramDateCompleted)}
                        </span>
                      }
                      error={errors.ProgramDateCompleted}
                      value={getFieldValue(data.ProgramDateCompleted)}
                      onChange={handleChange}
                      identical={compare("ProgramDateCompleted")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                </div>
              </div>
              <div className="container fst-italic text-white py-2 bg-pfs2">
                <h5>{getFieldLabel(formConfig.StaffHeader)}</h5>
              </div>
              <div className="container bg-safy-gray py-2">
                {getFieldLabel(formConfig.SurveyMethod)}
                <div className="row">
                  <div className="col py-2">
                    <FormRadioButton
                      name="SurveyMethod"
                      error={errors.SurveyMethod}
                      value={getFieldValue(data.SurveyMethod)}
                      options={[getFieldOptions(formConfig.SurveyMethod)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="SurveyMethod"
                      error={errors.SurveyMethod}
                      value={getFieldValue(data.SurveyMethod)}
                      options={[getFieldOptions(formConfig.SurveyMethod)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="SurveyMethod"
                      error={errors.SurveyMethod}
                      value={getFieldValue(data.SurveyMethod)}
                      options={[getFieldOptions(formConfig.SurveyMethod)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ReferralSource"
                      label={getFieldLabel(formConfig.ReferralSource)}
                      error={errors.ReferralSource}
                      value={getFieldValue(data.ReferralSource)}
                      options={getFieldOptions(data.ReferralSource)}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col">
                    {getFieldLabel(formConfig.ReportedToCPS)}
                    <div className="row">
                      <div className="col-8 py-2">
                        <FormRadioButton
                          name="ReportedToCPS"
                          value={getFieldValue(data.ReportedToCPS)}
                          options={[
                            getFieldOptions(formConfig.ReportedToCPS)[0],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        <FormRadioButton
                          name="ReportedToCPS"
                          value={getFieldValue(data.ReportedToCPS)}
                          options={[
                            getFieldOptions(formConfig.ReportedToCPS)[2],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-2 py-2">
                        <FormRadioButton
                          name="ReportedToCPS"
                          value={getFieldValue(data.ReportedToCPS)}
                          options={[
                            getFieldOptions(formConfig.ReportedToCPS)[1],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col py-2">
                        <FormRadioButton
                          name="ReportedToCPSWhen"
                          error={errors.ReportedToCPSWhen}
                          value={getFieldValue(data.ReportedToCPSWhen)}
                          options={[
                            getFieldOptions(formConfig.ReportedToCPSWhen)[0],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled ||
                            getFieldValue(data.ReportedToCPS) !== "B. Yes"
                          }
                        />
                      </div>
                      <div className="col py-2">
                        <FormRadioButton
                          name="ReportedToCPSWhen"
                          error={errors.ReportedToCPSWhen}
                          value={getFieldValue(data.ReportedToCPSWhen)}
                          options={[
                            getFieldOptions(formConfig.ReportedToCPSWhen)[1],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled ||
                            getFieldValue(data.ReportedToCPS) !== "B. Yes"
                          }
                        />
                      </div>
                      <div className="col-4 py-2">
                        <FormRadioButton
                          name="ReportedToCPSWhen"
                          error={errors.ReportedToCPSWhen}
                          value={getFieldValue(data.ReportedToCPSWhen)}
                          options={[
                            getFieldOptions(formConfig.ReportedToCPSWhen)[2],
                          ]}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled ||
                            getFieldValue(data.ReportedToCPS) !== "B. Yes"
                          }
                        />
                      </div>
                    </div>
                    {errors.ReportedToCPS !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.ReportedToCPS}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="container py-2">
                {getFieldLabel(formConfig.ReportSubstantiated)}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ReportSubstantiated"
                      value={getFieldValue(data.ReportSubstantiated)}
                      options={[
                        getFieldOptions(formConfig.ReportSubstantiated)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ReportSubstantiated"
                      value={getFieldValue(data.ReportSubstantiated)}
                      options={[
                        getFieldOptions(formConfig.ReportSubstantiated)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ReportSubstantiated"
                      value={getFieldValue(data.ReportSubstantiated)}
                      options={[
                        getFieldOptions(formConfig.ReportSubstantiated)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ReportSubstantiated"
                      value={getFieldValue(data.ReportSubstantiated)}
                      options={[
                        getFieldOptions(formConfig.ReportSubstantiated)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ReportSubstantiated"
                      value={getFieldValue(data.ReportSubstantiated)}
                      options={[
                        getFieldOptions(formConfig.ReportSubstantiated)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ReportSubstantiated"
                      value={getFieldValue(data.ReportSubstantiated)}
                      options={[
                        getFieldOptions(formConfig.ReportSubstantiated)[5],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.ReportSubstantiated !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ReportSubstantiated}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col">
                    {getFieldLabel(formConfig.ProgramType)}
                    <div className="row">
                      <div className="col py-2">
                        <FormCheckbox
                          padded={true}
                          name="ProgramAdvocacy"
                          label={getFieldLabel(formConfig.ProgramAdvocacy)}
                          checked={
                            getFieldValue(data.ProgramAdvocacy) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramRelationships"
                          label={getFieldLabel(formConfig.ProgramRelationships)}
                          checked={
                            getFieldValue(data.ProgramRelationships) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramVisiting"
                          label={getFieldLabel(formConfig.ProgramVisiting)}
                          checked={
                            getFieldValue(data.ProgramVisiting) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramHousing"
                          label={getFieldLabel(formConfig.ProgramHousing)}
                          checked={
                            getFieldValue(data.ProgramHousing) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <FormCheckbox
                          padded={true}
                          name="ProgramEducation"
                          label={getFieldLabel(formConfig.ProgramEducation)}
                          checked={
                            getFieldValue(data.ProgramEducation) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramInteraction"
                          label={getFieldLabel(formConfig.ProgramInteraction)}
                          checked={
                            getFieldValue(data.ProgramInteraction) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramSupport"
                          label={getFieldLabel(formConfig.ProgramSupport)}
                          checked={
                            getFieldValue(data.ProgramSupport) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramRespite"
                          label={getFieldLabel(formConfig.ProgramRespite)}
                          checked={
                            getFieldValue(data.ProgramRespite) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                      </div>
                      <div className="col py-2">
                        <FormCheckbox
                          padded={true}
                          name="ProgramResources"
                          label={getFieldLabel(formConfig.ProgramResources)}
                          checked={
                            getFieldValue(data.ProgramResources) === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramSkills"
                          label={getFieldLabel(formConfig.ProgramSkills)}
                          checked={getFieldValue(data.ProgramSkills) === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormCheckbox
                          padded={true}
                          name="ProgramOther"
                          label={getFieldLabel(formConfig.ProgramOther)}
                          checked={getFieldValue(data.ProgramOther) === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          divClassName="ms-3"
                        />
                        <FormInput
                          name="ProgramOtherDetails"
                          label={getFieldLabel(formConfig.ProgramOtherDetails)}
                          error={errors.ProgramOtherDetails}
                          value={getFieldValue(data.ProgramOtherDetails)}
                          onChange={handleChange}
                          identical={compare("ProgramOtherDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {errors.ProgramType !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ProgramType}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col">
                    {getFieldLabel(formConfig.Attendance)}
                    <div className="row">
                      <div className="col">
                        <b>{getFieldLabel(formConfig.PreTestAttendance)}</b>
                        <FormInput
                          name="PreTestAttendance"
                          label={data.PreTestAttendance?.sub}
                          error={errors.PreTestAttendance}
                          value={getFieldValue(data.PreTestAttendance)}
                          onChange={handleChange}
                          identical={compare("PreTestAttendance")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <b>{getFieldLabel(formConfig.PostTestAttendance)}</b>
                        <FormInput
                          name="PostTestAttendance"
                          label={data.PostTestAttendance?.sub}
                          error={errors.PostTestAttendance}
                          value={getFieldValue(data.PostTestAttendance)}
                          onChange={handleChange}
                          identical={compare("PostTestAttendance")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="container fst-italic text-white py-2 bg-pfs2">
                <h5>{getFieldLabel(formConfig.StaffFooter)}</h5>
              </div>
              <div className="container fst-italic py-2">
                <div className="row">
                  <div className="col">
                    <FormInput
                      name="AgencyId"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.AgencyId)}
                        </span>
                      }
                      error={errors.AgencyId}
                      value={getFieldValue(data.AgencyId)}
                      onChange={handleChange}
                      identical={compare("AgencyId")}
                      commentsHandler={handleComments}
                      disabled={true}
                    />
                  </div>
                  <div className="col">
                    <FormInput
                      name="ParticipantId"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.ParticipantId)}
                        </span>
                      }
                      error={errors.ParticipantId}
                      value={getFieldValue(data.ParticipantId)}
                      onChange={handleChange}
                      identical={compare("ParticipantId")}
                      commentsHandler={handleComments}
                      disabled={true}
                    />
                  </div>
                  <div className="col">
                    <FormInput
                      name="DateCompleted"
                      label={
                        <span className="fst-italic">
                          {getFieldLabel(formConfig.DateCompleted)}
                        </span>
                      }
                      error={errors.DateCompleted}
                      value={getFieldValue(data.DateCompleted)}
                      onChange={handleChange}
                      identical={compare("DateCompleted")}
                      commentsHandler={handleComments}
                      disabled={true}
                      type="date"
                    />
                  </div>
                </div>
              </div>
              <div className="container fst-italic text-white py-2 bg-pfs2">
                <h5>
                  {getFieldLabel(formConfig.ConfidentialHeader)}
                  <br />
                  <br />
                  {data.ConfidentialHeader?.label2}
                </h5>
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5"></div>
                  <div className="col py-2 text-center">
                    {data.FeelingColumns?.col1}
                  </div>
                  <div className="col py-2 text-center">
                    {data.FeelingColumns?.col2}
                  </div>
                  <div className="col py-2 text-center">
                    {data.FeelingColumns?.col3}
                  </div>
                  <div className="col py-2 text-center">
                    {data.FeelingColumns?.col4}
                  </div>
                  <div className="col py-2 text-center">
                    {data.FeelingColumns?.col5}
                  </div>
                </div>
              </div>
              <div className="container">
                <div className="row">
                  <div className="col-5 py-2">
                    {getFieldLabel(formConfig.GoodFuture)}
                  </div>
                  <div className="col py-2 text-center">
                    <FormRadioButton
                      name="GoodFuture"
                      value={getFieldValue(data.GoodFuture)}
                      options={[getFieldOptions(formConfig.GoodFuture)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 text-center">
                    <FormRadioButton
                      name="GoodFuture"
                      value={getFieldValue(data.GoodFuture)}
                      options={[getFieldOptions(formConfig.GoodFuture)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 text-center">
                    <FormRadioButton
                      name="GoodFuture"
                      value={getFieldValue(data.GoodFuture)}
                      options={[getFieldOptions(formConfig.GoodFuture)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 text-center">
                    <FormRadioButton
                      name="GoodFuture"
                      value={getFieldValue(data.GoodFuture)}
                      options={[getFieldOptions(formConfig.GoodFuture)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 text-center">
                    <FormRadioButton
                      name="GoodFuture"
                      value={getFieldValue(data.GoodFuture)}
                      options={[getFieldOptions(formConfig.GoodFuture)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.GoodFuture !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.GoodFuture}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.FamilyListens)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="FamilyListens"
                      value={getFieldValue(data.FamilyListens)}
                      options={[getFieldOptions(formConfig.FamilyListens)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="FamilyListens"
                      value={getFieldValue(data.FamilyListens)}
                      options={[getFieldOptions(formConfig.FamilyListens)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="FamilyListens"
                      value={getFieldValue(data.FamilyListens)}
                      options={[getFieldOptions(formConfig.FamilyListens)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="FamilyListens"
                      value={getFieldValue(data.FamilyListens)}
                      options={[getFieldOptions(formConfig.FamilyListens)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="FamilyListens"
                      value={getFieldValue(data.FamilyListens)}
                      options={[getFieldOptions(formConfig.FamilyListens)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.FamilyListens !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.FamilyListens}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.SpecialMoments)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SpecialMoments"
                      value={getFieldValue(data.SpecialMoments)}
                      options={[getFieldOptions(formConfig.SpecialMoments)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SpecialMoments"
                      value={getFieldValue(data.SpecialMoments)}
                      options={[getFieldOptions(formConfig.SpecialMoments)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SpecialMoments"
                      value={getFieldValue(data.SpecialMoments)}
                      options={[getFieldOptions(formConfig.SpecialMoments)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SpecialMoments"
                      value={getFieldValue(data.SpecialMoments)}
                      options={[getFieldOptions(formConfig.SpecialMoments)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SpecialMoments"
                      value={getFieldValue(data.SpecialMoments)}
                      options={[getFieldOptions(formConfig.SpecialMoments)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.SpecialMoments !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.SpecialMoments}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.IntentToMisbehave)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="IntentToMisbehave"
                      value={getFieldValue(data.IntentToMisbehave)}
                      options={[
                        getFieldOptions(formConfig.IntentToMisbehave)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="IntentToMisbehave"
                      value={getFieldValue(data.IntentToMisbehave)}
                      options={[
                        getFieldOptions(formConfig.IntentToMisbehave)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="IntentToMisbehave"
                      value={getFieldValue(data.IntentToMisbehave)}
                      options={[
                        getFieldOptions(formConfig.IntentToMisbehave)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="IntentToMisbehave"
                      value={getFieldValue(data.IntentToMisbehave)}
                      options={[
                        getFieldOptions(formConfig.IntentToMisbehave)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="IntentToMisbehave"
                      value={getFieldValue(data.IntentToMisbehave)}
                      options={[
                        getFieldOptions(formConfig.IntentToMisbehave)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.IntentToMisbehave !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.IntentToMisbehave}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.ConstantStop)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ConstantStop"
                      value={getFieldValue(data.ConstantStop)}
                      options={[getFieldOptions(formConfig.ConstantStop)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ConstantStop"
                      value={getFieldValue(data.ConstantStop)}
                      options={[getFieldOptions(formConfig.ConstantStop)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ConstantStop"
                      value={getFieldValue(data.ConstantStop)}
                      options={[getFieldOptions(formConfig.ConstantStop)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ConstantStop"
                      value={getFieldValue(data.ConstantStop)}
                      options={[getFieldOptions(formConfig.ConstantStop)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ConstantStop"
                      value={getFieldValue(data.ConstantStop)}
                      options={[getFieldOptions(formConfig.ConstantStop)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.ConstantStop !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ConstantStop}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.PowerStruggles)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="PowerStruggles"
                      value={getFieldValue(data.PowerStruggles)}
                      options={[getFieldOptions(formConfig.PowerStruggles)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="PowerStruggles"
                      value={getFieldValue(data.PowerStruggles)}
                      options={[getFieldOptions(formConfig.PowerStruggles)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="PowerStruggles"
                      value={getFieldValue(data.PowerStruggles)}
                      options={[getFieldOptions(formConfig.PowerStruggles)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="PowerStruggles"
                      value={getFieldValue(data.PowerStruggles)}
                      options={[getFieldOptions(formConfig.PowerStruggles)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="PowerStruggles"
                      value={getFieldValue(data.PowerStruggles)}
                      options={[getFieldOptions(formConfig.PowerStruggles)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.PowerStruggles !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.PowerStruggles}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.EmotionalResponse)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="EmotionalResponse"
                      value={getFieldValue(data.EmotionalResponse)}
                      options={[
                        getFieldOptions(formConfig.EmotionalResponse)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="EmotionalResponse"
                      value={getFieldValue(data.EmotionalResponse)}
                      options={[
                        getFieldOptions(formConfig.EmotionalResponse)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="EmotionalResponse"
                      value={getFieldValue(data.EmotionalResponse)}
                      options={[
                        getFieldOptions(formConfig.EmotionalResponse)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="EmotionalResponse"
                      value={getFieldValue(data.EmotionalResponse)}
                      options={[
                        getFieldOptions(formConfig.EmotionalResponse)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="EmotionalResponse"
                      value={getFieldValue(data.EmotionalResponse)}
                      options={[
                        getFieldOptions(formConfig.EmotionalResponse)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.EmotionalResponse !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.EmotionalResponse}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.BeliefInMe)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="BeliefInMe"
                      value={getFieldValue(data.BeliefInMe)}
                      options={[getFieldOptions(formConfig.BeliefInMe)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="BeliefInMe"
                      value={getFieldValue(data.BeliefInMe)}
                      options={[getFieldOptions(formConfig.BeliefInMe)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="BeliefInMe"
                      value={getFieldValue(data.BeliefInMe)}
                      options={[getFieldOptions(formConfig.BeliefInMe)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="BeliefInMe"
                      value={getFieldValue(data.BeliefInMe)}
                      options={[getFieldOptions(formConfig.BeliefInMe)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="BeliefInMe"
                      value={getFieldValue(data.BeliefInMe)}
                      options={[getFieldOptions(formConfig.BeliefInMe)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.BeliefInMe !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.BeliefInMe}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.LifeAdvice)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="LifeAdvice"
                      value={getFieldValue(data.LifeAdvice)}
                      options={[getFieldOptions(formConfig.LifeAdvice)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="LifeAdvice"
                      value={getFieldValue(data.LifeAdvice)}
                      options={[getFieldOptions(formConfig.LifeAdvice)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="LifeAdvice"
                      value={getFieldValue(data.LifeAdvice)}
                      options={[getFieldOptions(formConfig.LifeAdvice)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="LifeAdvice"
                      value={getFieldValue(data.LifeAdvice)}
                      options={[getFieldOptions(formConfig.LifeAdvice)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="LifeAdvice"
                      value={getFieldValue(data.LifeAdvice)}
                      options={[getFieldOptions(formConfig.LifeAdvice)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.LifeAdvice !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.LifeAdvice}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.SupportiveFriends)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SupportiveFriends"
                      value={getFieldValue(data.SupportiveFriends)}
                      options={[
                        getFieldOptions(formConfig.SupportiveFriends)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SupportiveFriends"
                      value={getFieldValue(data.SupportiveFriends)}
                      options={[
                        getFieldOptions(formConfig.SupportiveFriends)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SupportiveFriends"
                      value={getFieldValue(data.SupportiveFriends)}
                      options={[
                        getFieldOptions(formConfig.SupportiveFriends)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SupportiveFriends"
                      value={getFieldValue(data.SupportiveFriends)}
                      options={[
                        getFieldOptions(formConfig.SupportiveFriends)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="SupportiveFriends"
                      value={getFieldValue(data.SupportiveFriends)}
                      options={[
                        getFieldOptions(formConfig.SupportiveFriends)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.SupportiveFriends !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.SupportiveFriends}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.ShortNotice)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ShortNotice"
                      value={getFieldValue(data.ShortNotice)}
                      options={[getFieldOptions(formConfig.ShortNotice)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ShortNotice"
                      value={getFieldValue(data.ShortNotice)}
                      options={[getFieldOptions(formConfig.ShortNotice)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ShortNotice"
                      value={getFieldValue(data.ShortNotice)}
                      options={[getFieldOptions(formConfig.ShortNotice)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ShortNotice"
                      value={getFieldValue(data.ShortNotice)}
                      options={[getFieldOptions(formConfig.ShortNotice)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="ShortNotice"
                      value={getFieldValue(data.ShortNotice)}
                      options={[getFieldOptions(formConfig.ShortNotice)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.ShortNotice !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ShortNotice}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                {getFieldLabel(formConfig.Trustworthy)}
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="AdviceMoney"
                      label={getFieldLabel(formConfig.AdviceMoney)}
                      checked={getFieldValue(data.AdviceMoney) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="AdviceFood"
                      label={getFieldLabel(formConfig.AdviceFood)}
                      checked={getFieldValue(data.AdviceFood) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="AdviceParenting"
                      label={getFieldLabel(formConfig.AdviceParenting)}
                      checked={getFieldValue(data.AdviceParenting) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="AdviceRelationships"
                      label={getFieldLabel(formConfig.AdviceRelationships)}
                      checked={
                        getFieldValue(data.AdviceRelationships) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="AdviceStress"
                      label={getFieldLabel(formConfig.AdviceStress)}
                      checked={getFieldValue(data.AdviceStress) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="AdviceNone"
                      label={getFieldLabel(formConfig.AdviceNone)}
                      checked={getFieldValue(data.AdviceNone) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                {errors.Trustworthy !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.Trustworthy}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container fst-italic text-white py-2 mt-5 bg-pfs2">
                <h5>{getFieldLabel(formConfig.OrgExperience)}</h5>
              </div>
              <div className="container mt-4">
                <div className="row">
                  <div className="col-5"></div>
                  <div className="col py-2 text-center">
                    {data.OrgExperience?.col1}
                  </div>
                  <div className="col py-2 text-center">
                    {data.OrgExperience?.col2}
                  </div>
                  <div className="col py-2 text-center">
                    {data.OrgExperience?.col3}
                  </div>
                  <div className="col py-2 text-center">
                    {data.OrgExperience?.col4}
                  </div>
                  <div className="col py-2 text-center">
                    {data.OrgExperience?.col5}
                  </div>
                </div>
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.StaffUnderstands)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="StaffUnderstands"
                      value={getFieldValue(data.StaffUnderstands)}
                      options={[
                        getFieldOptions(formConfig.StaffUnderstands)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="StaffUnderstands"
                      value={getFieldValue(data.StaffUnderstands)}
                      options={[
                        getFieldOptions(formConfig.StaffUnderstands)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="StaffUnderstands"
                      value={getFieldValue(data.StaffUnderstands)}
                      options={[
                        getFieldOptions(formConfig.StaffUnderstands)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="StaffUnderstands"
                      value={getFieldValue(data.StaffUnderstands)}
                      options={[
                        getFieldOptions(formConfig.StaffUnderstands)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="StaffUnderstands"
                      value={getFieldValue(data.StaffUnderstands)}
                      options={[
                        getFieldOptions(formConfig.StaffUnderstands)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.StaffUnderstands !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.StaffUnderstands}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.NoBelief)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="NoBelief"
                      value={getFieldValue(data.NoBelief)}
                      options={[getFieldOptions(formConfig.NoBelief)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="NoBelief"
                      value={getFieldValue(data.NoBelief)}
                      options={[getFieldOptions(formConfig.NoBelief)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="NoBelief"
                      value={getFieldValue(data.NoBelief)}
                      options={[getFieldOptions(formConfig.NoBelief)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="NoBelief"
                      value={getFieldValue(data.NoBelief)}
                      options={[getFieldOptions(formConfig.NoBelief)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="NoBelief"
                      value={getFieldValue(data.NoBelief)}
                      options={[getFieldOptions(formConfig.NoBelief)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.NoBelief !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.NoBelief}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.Misunderstood)}
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="Misunderstood"
                      value={getFieldValue(data.Misunderstood)}
                      options={[getFieldOptions(formConfig.Misunderstood)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="Misunderstood"
                      value={getFieldValue(data.Misunderstood)}
                      options={[getFieldOptions(formConfig.Misunderstood)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="Misunderstood"
                      value={getFieldValue(data.Misunderstood)}
                      options={[getFieldOptions(formConfig.Misunderstood)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="Misunderstood"
                      value={getFieldValue(data.Misunderstood)}
                      options={[getFieldOptions(formConfig.Misunderstood)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col text-center">
                    <FormRadioButton
                      name="Misunderstood"
                      value={getFieldValue(data.Misunderstood)}
                      options={[getFieldOptions(formConfig.Misunderstood)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.Misunderstood !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.Misunderstood}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container fst-italic text-white py-2 mt-5 bg-pfs2">
                <h5>{getFieldLabel(formConfig.AffordHeader)}</h5>
              </div>
              <div className="container mt-4">
                {getFieldLabel(formConfig.UnableToPay)}
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayRent"
                      label={getFieldLabel(formConfig.PayRent)}
                      checked={getFieldValue(data.PayRent) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayDaycare"
                      label={getFieldLabel(formConfig.PayDaycare)}
                      checked={getFieldValue(data.PayDaycare) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayTransportation"
                      label={getFieldLabel(formConfig.PayTransportation)}
                      checked={getFieldValue(data.PayTransportation) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayUtilities"
                      label={getFieldLabel(formConfig.PayUtilities)}
                      checked={getFieldValue(data.PayUtilities) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayMedical"
                      label={getFieldLabel(formConfig.PayMedical)}
                      checked={getFieldValue(data.PayMedical) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayAll"
                      label={getFieldLabel(formConfig.PayAll)}
                      checked={getFieldValue(data.PayAll) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayGroceries"
                      label={getFieldLabel(formConfig.PayGroceries)}
                      checked={getFieldValue(data.PayGroceries) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PayBasic"
                      label={getFieldLabel(formConfig.PayBasic)}
                      checked={getFieldValue(data.PayBasic) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2"></div>
                </div>
                {errors.UnableToPay !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.UnableToPay}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray mt-4 py-2">
                {getFieldLabel(formConfig.PastYear)}
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastDelay"
                      label={getFieldLabel(formConfig.PastDelay)}
                      checked={getFieldValue(data.PastDelay) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastShelter"
                      label={getFieldLabel(formConfig.PastShelter)}
                      checked={getFieldValue(data.PastShelter) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastTransporation"
                      label={getFieldLabel(formConfig.PastTransporation)}
                      checked={getFieldValue(data.PastTransporation) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastEvicted"
                      label={getFieldLabel(formConfig.PastEvicted)}
                      checked={getFieldValue(data.PastEvicted) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastRoommate"
                      label={getFieldLabel(formConfig.PastRoommate)}
                      checked={getFieldValue(data.PastRoommate) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastUnemployment"
                      label={getFieldLabel(formConfig.PastUnemployment)}
                      checked={getFieldValue(data.PastUnemployment) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-8"></div>
                  <div className="col py-2">
                    <FormCheckbox
                      padded={true}
                      name="PastNone"
                      label={getFieldLabel(formConfig.PastNone)}
                      checked={getFieldValue(data.PastNone) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                {errors.PastYear !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.PastYear}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container mt-4">
                <div className="row">
                  <div className="col-5"></div>
                  <div className="col py-2">{data.AffordColumns?.col1}</div>
                  <div className="col py-2">{data.AffordColumns?.col2}</div>
                  <div className="col py-2">{data.AffordColumns?.col3}</div>
                  <div className="col py-2">{data.AffordColumns?.col4}</div>
                  <div className="col py-2">{data.AffordColumns?.col5}</div>
                </div>
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.TroubleAffording)}
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="TroubleAffording"
                      value={getFieldValue(data.TroubleAffording)}
                      options={[
                        getFieldOptions(formConfig.TroubleAffording)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="TroubleAffording"
                      value={getFieldValue(data.TroubleAffording)}
                      options={[
                        getFieldOptions(formConfig.TroubleAffording)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="TroubleAffording"
                      value={getFieldValue(data.TroubleAffording)}
                      options={[
                        getFieldOptions(formConfig.TroubleAffording)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="TroubleAffording"
                      value={getFieldValue(data.TroubleAffording)}
                      options={[
                        getFieldOptions(formConfig.TroubleAffording)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="TroubleAffording"
                      value={getFieldValue(data.TroubleAffording)}
                      options={[
                        getFieldOptions(formConfig.TroubleAffording)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.TroubleAffording !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.TroubleAffording}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container py-2">
                <div className="row">
                  <div className="col-5">
                    {getFieldLabel(formConfig.AffordFood)}
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="AffordFood"
                      value={getFieldValue(data.AffordFood)}
                      options={[getFieldOptions(formConfig.AffordFood)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="AffordFood"
                      value={getFieldValue(data.AffordFood)}
                      options={[getFieldOptions(formConfig.AffordFood)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="AffordFood"
                      value={getFieldValue(data.AffordFood)}
                      options={[getFieldOptions(formConfig.AffordFood)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="AffordFood"
                      value={getFieldValue(data.AffordFood)}
                      options={[getFieldOptions(formConfig.AffordFood)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="AffordFood"
                      value={getFieldValue(data.AffordFood)}
                      options={[getFieldOptions(formConfig.AffordFood)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.AffordFood !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.AffordFood}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <ExpandDiv id="Scores" title="Subscale Scores">
                <Multiselect
                  data={allAssessmentDatesUnique}
                  value={selectedAssessmentDates}
                  onChange={(selected) =>
                    setSelectedAssessmentDates(
                      selected.sort((a, b) => moment(a) - moment(b))
                    )
                  }
                />
                {selectedAssessmentDates.length > 0 && chartData ? (
                  <LineGraph options={graphOptions} data={chartData} />
                ) : null}
              </ExpandDiv>
              <div className="container fst-italic text-white py-2 mt-5 bg-pfs2">
                <h5>{getFieldLabel(formConfig.ChildHeader)}</h5>
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="Child1Sex"
                      label={getFieldLabel(formConfig.Child1Sex)}
                      error={errors.Child1Sex}
                      value={getFieldValue(data.Child1Sex)}
                      options={getFieldOptions(data.Child1Sex)}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormInput
                      name="Child1Age"
                      label={getFieldLabel(formConfig.Child1Age)}
                      error={errors.Child1Age}
                      value={getFieldValue(data.Child1Age)}
                      onChange={handleChange}
                      identical={compare("Child1Age")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="Child1InHome"
                      label={getFieldLabel(formConfig.Child1InHome)}
                      error={errors.Child1InHome}
                      value={getFieldValue(data.Child1InHome)}
                      options={getFieldOptions(data.Child1InHome)}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div>
                  {getFieldLabel(formConfig.Child1Relationship)}
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[0],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[3],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[6],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[1],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[4],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[7],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[2],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child1Relationship"
                        value={getFieldValue(data.Child1Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child1Relationship)[5],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                  {errors.Child1Relationship !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.Child1Relationship}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="container my-5">
                <FormRadioButton
                  name="Child2Sex"
                  label={getFieldLabel(formConfig.Child2Sex)}
                  error={errors.Child2Sex}
                  value={getFieldValue(data.Child2Sex)}
                  options={getFieldOptions(data.Child2Sex)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="Child2Age"
                  label={getFieldLabel(formConfig.Child2Age)}
                  error={errors.Child2Age}
                  value={getFieldValue(data.Child2Age)}
                  onChange={handleChange}
                  identical={compare("Child2Age")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="Child2InHome"
                  label={getFieldLabel(formConfig.Child2InHome)}
                  error={errors.Child2InHome}
                  value={getFieldValue(data.Child2InHome)}
                  options={getFieldOptions(data.Child2InHome)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div>
                  {getFieldLabel(formConfig.Child2Relationship)}
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[0],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[3],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[6],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[1],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[4],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[7],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[2],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child2Relationship"
                        value={getFieldValue(data.Child2Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child2Relationship)[5],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                  {errors.Child2Relationship !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.Child2Relationship}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="container bg-safy-gray py-2">
                <FormRadioButton
                  name="Child3Sex"
                  label={getFieldLabel(formConfig.Child3Sex)}
                  error={errors.Child3Sex}
                  value={getFieldValue(data.Child3Sex)}
                  options={getFieldOptions(data.Child3Sex)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="Child3Age"
                  label={getFieldLabel(formConfig.Child3Age)}
                  error={errors.Child3Age}
                  value={getFieldValue(data.Child3Age)}
                  onChange={handleChange}
                  identical={compare("Child3Age")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="Child3InHome"
                  label={getFieldLabel(formConfig.Child3InHome)}
                  error={errors.Child3InHome}
                  value={getFieldValue(data.Child3InHome)}
                  options={getFieldOptions(data.Child3InHome)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div>
                  {getFieldLabel(formConfig.Child3Relationship)}
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[0],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[3],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[6],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[1],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[4],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[7],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[2],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child3Relationship"
                        value={getFieldValue(data.Child3Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child3Relationship)[5],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                  {errors.Child3Relationship !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.Child3Relationship}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="container my-5">
                <FormRadioButton
                  name="Child4Sex"
                  label={getFieldLabel(formConfig.Child4Sex)}
                  error={errors.Child4Sex}
                  value={getFieldValue(data.Child4Sex)}
                  options={getFieldOptions(data.Child4Sex)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormInput
                  name="Child4Age"
                  label={getFieldLabel(formConfig.Child4Age)}
                  error={errors.Child4Age}
                  value={getFieldValue(data.Child4Age)}
                  onChange={handleChange}
                  identical={compare("Child4Age")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormRadioButton
                  name="Child4InHome"
                  label={getFieldLabel(formConfig.Child4InHome)}
                  error={errors.Child4InHome}
                  value={getFieldValue(data.Child4InHome)}
                  options={getFieldOptions(data.Child4InHome)}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div>
                  {getFieldLabel(formConfig.Child4Relationship)}
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[0],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[3],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[6],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[1],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[4],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[7],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[2],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormRadioButton
                        name="Child4Relationship"
                        value={getFieldValue(data.Child4Relationship)}
                        options={[
                          getFieldOptions(formConfig.Child4Relationship)[5],
                        ]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                  {errors.Child4Relationship !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.Child4Relationship}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="container fst-italic text-white py-2 mt-5 bg-pfs2">
                <h5>{getFieldLabel(formConfig.HouseholdHeader)}</h5>
              </div>
              <div className="container pt-2">
                <div className="row">
                  <div className="col d-flex">
                    <label htmlFor="ParentGender">
                      {getFieldLabel(formConfig.ParentGender)}
                    </label>
                    <FormRadioButton
                      name="ParentGender"
                      error={errors.ParentGender}
                      value={getFieldValue(data.ParentGender)}
                      options={getFieldOptions(data.ParentGender)}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="flex-fill"
                    />
                  </div>
                </div>
              </div>
              <div className="container bg-safy-gray py-2">
                <div className="row">
                  <div className="col">
                    <FormInput
                      name="ParentAge"
                      label={getFieldLabel(formConfig.ParentAge)}
                      error={errors.ParentAge}
                      value={getFieldValue(data.ParentAge)}
                      onChange={handleChange}
                      identical={compare("ParentAge")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <div className="container">
                {getFieldLabel(formConfig.ParentLanguage)}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[6]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col"></div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentLanguage"
                      value={getFieldValue(data.ParentLanguage)}
                      options={[getFieldOptions(formConfig.ParentLanguage)[5]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col"></div>
                </div>
                {errors.ParentLanguage !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ParentLanguage}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                {getFieldLabel(formConfig.ParentRace)}
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="NativeAmerican"
                      label={getFieldLabel(formConfig.NativeAmerican)}
                      checked={getFieldValue(data.NativeAmerican) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="Hispanic"
                      label={getFieldLabel(formConfig.Hispanic)}
                      checked={getFieldValue(data.Hispanic) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="MultiRacial"
                      label={getFieldLabel(formConfig.MultiRacial)}
                      checked={getFieldValue(data.MultiRacial) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="Asian"
                      label={getFieldLabel(formConfig.Asian)}
                      checked={getFieldValue(data.Asian) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="MiddleEastern"
                      label={getFieldLabel(formConfig.MiddleEastern)}
                      checked={getFieldValue(data.MiddleEastern) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="OtherRace"
                      label={getFieldLabel(formConfig.OtherRace)}
                      checked={getFieldValue(data.OtherRace) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    {getFieldValue(data.OtherRace) === "true" ? (
                      <FormInput
                        name="OtherRaceDetails"
                        error={errors.OtherRaceDetails}
                        value={getFieldValue(data.OtherRaceDetails)}
                        onChange={handleChange}
                        identical={compare("OtherRaceDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="AfricanAmerican"
                      label={getFieldLabel(formConfig.AfricanAmerican)}
                      checked={getFieldValue(data.AfricanAmerican) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="NativeHawaiian"
                      label={getFieldLabel(formConfig.NativeHawaiian)}
                      checked={getFieldValue(data.NativeHawaiian) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col"></div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="AfricanNational"
                      label={getFieldLabel(formConfig.AfricanNational)}
                      checked={getFieldValue(data.AfricanNational) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="White"
                      label={getFieldLabel(formConfig.White)}
                      checked={getFieldValue(data.White) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col"></div>
                </div>
                {errors.ParentRace !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ParentRace}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container">
                {getFieldLabel(formConfig.ParentRelationship)}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ParentRelationship"
                      value={getFieldValue(data.ParentRelationship)}
                      options={[
                        getFieldOptions(formConfig.ParentRelationship)[0],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentRelationship"
                      value={getFieldValue(data.ParentRelationship)}
                      options={[
                        getFieldOptions(formConfig.ParentRelationship)[2],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentRelationship"
                      value={getFieldValue(data.ParentRelationship)}
                      options={[
                        getFieldOptions(formConfig.ParentRelationship)[4],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="ParentRelationship"
                      value={getFieldValue(data.ParentRelationship)}
                      options={[
                        getFieldOptions(formConfig.ParentRelationship)[1],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentRelationship"
                      value={getFieldValue(data.ParentRelationship)}
                      options={[
                        getFieldOptions(formConfig.ParentRelationship)[3],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="ParentRelationship"
                      value={getFieldValue(data.ParentRelationship)}
                      options={[
                        getFieldOptions(formConfig.ParentRelationship)[5],
                      ]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {errors.ParentRelationship !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.ParentRelationship}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                {getFieldLabel(formConfig.FamilyHousing)}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyHousing"
                      value={getFieldValue(data.FamilyHousing)}
                      options={[getFieldOptions(formConfig.FamilyHousing)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyHousing"
                      value={getFieldValue(data.FamilyHousing)}
                      options={[getFieldOptions(formConfig.FamilyHousing)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyHousing"
                      value={getFieldValue(data.FamilyHousing)}
                      options={[getFieldOptions(formConfig.FamilyHousing)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyHousing"
                      value={getFieldValue(data.FamilyHousing)}
                      options={[getFieldOptions(formConfig.FamilyHousing)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyHousing"
                      value={getFieldValue(data.FamilyHousing)}
                      options={[getFieldOptions(formConfig.FamilyHousing)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col"></div>
                </div>
                {errors.FamilyHousing !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.FamilyHousing}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container">
                {getFieldLabel(formConfig.FamilyIncome)}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[6]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col"></div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyIncome"
                      value={getFieldValue(data.FamilyIncome)}
                      options={[getFieldOptions(formConfig.FamilyIncome)[5]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col"></div>
                </div>
                {errors.FamilyIncome !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.FamilyIncome}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container bg-safy-gray py-2">
                {getFieldLabel(formConfig.FamilyEducation)}
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[0]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[3]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[6]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[1]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[4]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[7]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[2]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[5]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                  <div className="col">
                    <FormRadioButton
                      name="FamilyEducation"
                      value={getFieldValue(data.FamilyEducation)}
                      options={[getFieldOptions(formConfig.FamilyEducation)[8]]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      optionsDivClassName="d-flex"
                      optionsLabelStyle={{
                        paddingLeft: "1.25em",
                        textIndent: "-1.25em",
                      }}
                    />
                  </div>
                </div>
                {errors.FamilyEducation !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.FamilyEducation}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="container">
                {getFieldLabel(formConfig.CurrentlyReceive)}
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveSNAP"
                      label={getFieldLabel(formConfig.ReceiveSNAP)}
                      checked={getFieldValue(data.ReceiveSNAP) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveSSDI"
                      label={getFieldLabel(formConfig.ReceiveSSDI)}
                      checked={getFieldValue(data.ReceiveSSDI) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveMedicaid"
                      label={getFieldLabel(formConfig.ReceiveMedicaid)}
                      checked={getFieldValue(data.ReceiveMedicaid) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveEITC"
                      label={getFieldLabel(formConfig.ReceiveEITC)}
                      checked={getFieldValue(data.ReceiveEITC) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveTANF"
                      label={getFieldLabel(formConfig.ReceiveTANF)}
                      checked={getFieldValue(data.ReceiveTANF) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveHeadStart"
                      label={getFieldLabel(formConfig.ReceiveHeadStart)}
                      checked={getFieldValue(data.ReceiveHeadStart) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveUnemployment"
                      label={getFieldLabel(formConfig.ReceiveUnemployment)}
                      checked={
                        getFieldValue(data.ReceiveUnemployment) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveStateInsurance"
                      label={getFieldLabel(formConfig.ReceiveStateInsurance)}
                      checked={
                        getFieldValue(data.ReceiveStateInsurance) === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveSSI"
                      label={getFieldLabel(formConfig.ReceiveSSI)}
                      checked={getFieldValue(data.ReceiveSSI) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveNone"
                      label={getFieldLabel(formConfig.ReceiveNone)}
                      checked={getFieldValue(data.ReceiveNone) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col"></div>
                  <div className="col">
                    <FormCheckbox
                      padded={true}
                      name="ReceiveOther"
                      label={getFieldLabel(formConfig.ReceiveOther)}
                      checked={getFieldValue(data.ReceiveOther) === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SAFYPFS2Assessment;
